import {Injectable} from '@angular/core';
import {EvaluationMatrixRowResponse, ParticipatingToolResponse} from '@frontend/shared-api';
import {EnrichedEvaluationDataResponse} from '../../pages/customer/project-sequence/chapter/chapter-7-rfd/step-7-5-provider-evaluation/evaluation-models';
import {getMaxScore, getScoreFromRating} from '../utils/rfd-evaluation-score.utils';

export interface RfdProviderScore {
  toolId: number;
  hrToolDirectusId: string;
  overall: number;
  percentage: number;
  subcriteriaScores: Map<string, number>;
  agendaScores: Map<number, number>;
}

@Injectable({
  providedIn: 'root',
})
export class RfdScoreCalculationService {
  calculateProviderScores(
    data: EnrichedEvaluationDataResponse,
    averageUserIds: number[],
    isExponentialScale: boolean,
  ): RfdProviderScore[] {
    const matrixRowById = this.buildMatrixRowMap(data);
    const eligibleUserIds = new Set<number>(averageUserIds);
    const maxScore = getMaxScore(isExponentialScale);

    return (data.tools ?? []).map((tool: ParticipatingToolResponse) => {
      const ratingsMap = this.buildAverageRatingsMap(
        data,
        tool.id,
        eligibleUserIds,
        matrixRowById,
        isExponentialScale,
      );
      const subcriteriaScores = this.buildSubcriteriaScores(data, ratingsMap);
      const agendaScores = this.buildAgendaScores(data, ratingsMap);
      const overall = this.calculateOverall(data, subcriteriaScores);

      return {
        toolId: tool.id,
        hrToolDirectusId: tool.hrToolDirectusId,
        overall,
        percentage: maxScore > 0 ? (overall / maxScore) * 100 : 0,
        subcriteriaScores,
        agendaScores,
      };
    });
  }

  private buildMatrixRowMap(
    data: EnrichedEvaluationDataResponse,
  ): Map<number, EvaluationMatrixRowResponse> {
    const map = new Map<number, EvaluationMatrixRowResponse>();
    for (const row of data.matrixRows ?? []) {
      if (row.id != null) {
        map.set(row.id, row);
      }
    }
    return map;
  }

  private buildAverageRatingsMap(
    data: EnrichedEvaluationDataResponse,
    toolId: number,
    eligibleUserIds: Set<number>,
    matrixRowById: Map<number, EvaluationMatrixRowResponse>,
    isExponentialScale: boolean,
  ): Map<string, number> {
    const sums = new Map<string, {sum: number; count: number}>();

    for (const e of data.userEvaluations ?? []) {
      if (e.participatingToolId == null || e.participatingToolId !== toolId) continue;
      if (e.userId == null || !eligibleUserIds.has(e.userId)) continue;
      if (e.rating == null || e.rating === -1 || e.matrixId == null) continue;

      const matrixRow = matrixRowById.get(e.matrixId);
      if (!matrixRow) continue;

      const agendaItemId = matrixRow.demonstrationAgendaItemId;
      const subcriteriaId = matrixRow.subcriteriaDirectusId;
      if (agendaItemId == null || !subcriteriaId) continue;

      const score = getScoreFromRating(e.rating, isExponentialScale);
      if (score == null) continue;

      const key = `${agendaItemId}-${subcriteriaId}`;
      const current = sums.get(key) ?? {sum: 0, count: 0};
      sums.set(key, {sum: current.sum + score, count: current.count + 1});
    }

    const ratingsMap = new Map<string, number>();
    sums.forEach((v, key) => {
      if (v.count > 0) {
        ratingsMap.set(key, v.sum / v.count);
      }
    });
    return ratingsMap;
  }

  private buildSubcriteriaScores(
    data: EnrichedEvaluationDataResponse,
    ratingsMap: Map<string, number>,
  ): Map<string, number> {
    const subcriteriaScores = new Map<string, number>();

    for (const subcriteria of data.subcriteria ?? []) {
      const ratings: number[] = [];
      for (const item of data.agendaItems ?? []) {
        if (item.id == null) continue;
        const score = ratingsMap.get(`${item.id}-${subcriteria.id}`);
        if (score != null) {
          ratings.push(score);
        }
      }
      if (ratings.length > 0) {
        subcriteriaScores.set(subcriteria.id, ratings.reduce((a, b) => a + b, 0) / ratings.length);
      }
    }

    return subcriteriaScores;
  }

  private buildAgendaScores(
    data: EnrichedEvaluationDataResponse,
    ratingsMap: Map<string, number>,
  ): Map<number, number> {
    const agendaScores = new Map<number, number>();

    for (const item of data.agendaItems ?? []) {
      if (item.id == null) continue;
      const ratings: number[] = [];
      for (const subcriteria of data.subcriteria ?? []) {
        const score = ratingsMap.get(`${item.id}-${subcriteria.id}`);
        if (score != null) {
          ratings.push(score);
        }
      }
      if (ratings.length > 0) {
        agendaScores.set(item.id, ratings.reduce((a, b) => a + b, 0) / ratings.length);
      }
    }

    return agendaScores;
  }

  private calculateOverall(
    data: EnrichedEvaluationDataResponse,
    subcriteriaScores: Map<string, number>,
  ): number {
    return (data.subcriteria ?? []).reduce((acc, subcriteria) => {
      const score = subcriteriaScores.get(subcriteria.id) ?? 0;
      const weight = subcriteria.weight ?? 0;
      return acc + score * weight;
    }, 0);
  }
}
